import { CommonModule } from '@angular/common';
import {
    Component, computed, inject, OnInit, signal, ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    FormBuilder, FormGroup, ReactiveFormsModule, Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '@nucleus/ui-base';

import { UserData, UserDataUpsertRequest } from '../../../api/user-data-service';
import { JsonPathViewerComponent } from '../../../common/json-path-viewer/json-path-viewer.component';
import { PageHeaderComponent } from '@nucleus/ui-base';
import { SchemaColumnBrowserComponent } from '../../../common/schema-column-browser/schema-column-browser.component';
import { UserDataService } from '../../../services/user-data/user-data.service';
import { UserDataTypeService } from '../../../services/userdata-type/userdata-type.service';
import { UserDataTypeReadOnlyStore } from '../../../services/userdata-type/userdata-type-readonly.store';
import { UserDataTypeAttribute } from '../../../services/userdata-type/userdata-type.models';

/**
 * Full-page UserData editor (TASK-177).
 *
 * <p>Layout: page-header (Back + Reset + Save) above a two-panel body.
 * <ul>
 *   <li>Left  ~60% — Identity + Resource card + Org scoping.</li>
 *   <li>Right ~40% — Schema fields: typed form built from the type's
 *       declared attributes via {@link SchemaFormComponent}. Each declared
 *       attribute renders as a labelled input (text / number / select);
 *       arrays of complex objects open in a per-field expansion panel.
 *       This is the intuitive surface — operators see labels for every
 *       field they're editing, no tree-traversal required.</li>
 * </ul>
 *
 * <p>The schema-form's value is deep-merged into a `data` JSON-string the
 * upsert request carries. A small "Raw JSON" expansion at the bottom of
 * the right panel surfaces the final payload as a read-only preview for
 * operators who want to spot-check the payload before saving.
 */
@Component({
    selector: 'app-user-data-edit-page',
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule,
        MatFormFieldModule, MatInputModule,
        MatSlideToggleModule, MatButtonModule, MatIconModule, MatTooltipModule,
        MatExpansionModule, MatProgressBarModule, MatTabsModule,
        JsonPathViewerComponent,
        PageHeaderComponent,
        SchemaColumnBrowserComponent,
    ],
    templateUrl: './user-data-edit-page.component.html',
    styleUrl: './user-data-edit-page.component.css',
})
export class UserDataEditPageComponent extends BaseComponent implements OnInit {

    private fb              = inject(FormBuilder);
    private route           = inject(ActivatedRoute);
    protected override router = inject(Router);
    private userDataService     = inject(UserDataService);
    private userDataTypeService = inject(UserDataTypeService);
    private readOnlyStore       = inject(UserDataTypeReadOnlyStore);

    readonly mode = signal<'create' | 'edit'>('create');
    readonly loaded = signal<UserData | null>(null);
    readonly loading = signal<boolean>(false);
    readonly saving = signal<boolean>(false);

    /** Declared attributes for the current type — drives the schema-form
     *  when present. */
    readonly attributes = signal<UserDataTypeAttribute[]>([]);

    /** Cached schema + the fingerprint it was built from. Lets us return
     *  the SAME schema object reference when the structural shape hasn't
     *  changed (declared attribute names/types + payload top-level keys),
     *  so the schema-form's ngOnChanges only fires changes['value'] on
     *  typical edits — preventing the form-rebuild churn that wipes
     *  in-progress chip-grid state. */
    private cachedSchema: any = null;
    private cachedSchemaFingerprint: string | null = null;

    /** Synthesized JSON-schema fed to <app-schema-form>. Built from the
     *  union of declared attributes + the keys observed in the current
     *  payload, so the operator always gets labelled inputs even when:
     *    • the type has zero declared attributes yet (new type, or row
     *      that pre-dates the attribute registry)
     *    • the type DOES have declared attributes but this particular
     *      row carries extra keys the schema doesn't know about
     *
     *  Declared attributes take precedence on type (string / integer /
     *  semver pattern); payload-only keys default to string for v1 —
     *  next ingest will promote them to typed INFERRED rules. Null is
     *  reserved for the case where the type has no attributes AND the
     *  payload is empty (the empty-state hint then shows). */
    readonly schemaForType = computed<any | null>(() => {
        const attrs = this.attributes();
        const payload = this.parsedJson();
        const hasAttrs    = attrs.length > 0;
        const hasPayload  = payload !== null && typeof payload === 'object' && !Array.isArray(payload);
        if (!hasAttrs && !hasPayload) {
            this.cachedSchema = null;
            this.cachedSchemaFingerprint = null;
            return null;
        }
        const fingerprint = this.fingerprintSchema(attrs, hasPayload ? payload : {});
        if (this.cachedSchemaFingerprint === fingerprint && this.cachedSchema) {
            return this.cachedSchema;
        }
        this.cachedSchemaFingerprint = fingerprint;
        this.cachedSchema = this.buildSchema(attrs, hasPayload ? payload : {});
        return this.cachedSchema;
    });

    /** Structural fingerprint = sorted "name:type" for declared attrs
     *  joined with sorted "key:typeof" for payload keys. Stable string,
     *  cheap equality. Nested object payload keys are walked one level
     *  deep — schema rebuilds aren't worth chasing deeper nesting churn. */
    private fingerprintSchema(attrs: UserDataTypeAttribute[], payload: any): string {
        const attrPart = attrs
            .map(a => `${a.payloadPath ?? a.name}:${a.valueType}`)
            .sort()
            .join('|');
        const keyPart = Object.keys(payload ?? {})
            .map(k => {
                const v = (payload as any)[k];
                if (Array.isArray(v)) return `${k}:array`;
                if (v === null || v === undefined) return `${k}:null`;
                return `${k}:${typeof v}`;
            })
            .sort()
            .join('|');
        return `${attrPart}::${keyPart}`;
    }
    /** Current parsed payload, projected as the schema-form's [value]. */
    readonly schemaValue = computed<any>(() => {
        const schema = this.schemaForType();
        if (!schema) return null;
        return this.parsedJson() ?? {};
    });

    readonly form: FormGroup;
    /** Reactive signal mirror of the data control — feeds parsedJson(). */
    private readonly dataText;
    /** Parsed JSON tree, surfaced via ngx-json-viewer on the Payload tab's
     *  left sub-panel as a live read-only preview. */
    readonly parsedJson;

    /** True = JSON sub-panel renders the editable textarea; false = the
     *  read-only json-path-viewer. Toggled via the pencil/check button on
     *  the JSON sub-panel header. */
    readonly editingJson = signal<boolean>(false);

    /** JSON path of the schema-form row currently in focus — drives the
     *  highlight + scroll on the JSON viewer on the left. Set on (focusPath)
     *  events bubbled up from app-schema-form. */
    readonly focusedPath = signal<string | null>(null);

    /** Toggle for the left JSON viewer pane. When false, the column-browser
     *  occupies the full pane width — useful when the operator wants the
     *  most room to edit and doesn't need the JSON tree visible. */
    readonly showJsonPane = signal<boolean>(true);

    toggleJsonPane(): void {
        this.showJsonPane.update(v => !v);
    }

    @ViewChild(SchemaColumnBrowserComponent) private browser?: SchemaColumnBrowserComponent;

    /** JSON-viewer (nodeClick) — drive the column browser into that path.
     *  For a leaf path, navigate to the *parent* column (so the leaf row is
     *  visible) and let focusPath highlight it. For a container path,
     *  navigate INTO it. */
    onJsonNodeClick(jsonPath: string): void {
        const segs = this.jsonPathToColumnSegments(jsonPath);
        this.focusedPath.set(jsonPath);
        if (!this.browser) return;

        // If the clicked path resolves to a leaf, drop the last segment so
        // the column browser opens the *parent* container — the leaf row
        // sits inside that column.
        const trimmed = this.isLeafPath(jsonPath) ? segs.slice(0, -1) : segs;
        this.browser.goTo(trimmed);
    }

    /** "$.interfaces[0].name" → ["interfaces", "[0]", "name"] — the segment
     *  form the column browser's activePath consumes (bracket indices kept
     *  as "[N]" strings). */
    private jsonPathToColumnSegments(jsonPath: string): string[] {
        const rest = jsonPath.startsWith('$') ? jsonPath.slice(1) : jsonPath;
        const segs: string[] = [];
        const regex = /\.([^.\[\]]+)|\[(\d+)\]/g;
        let m: RegExpExecArray | null;
        while ((m = regex.exec(rest)) !== null) {
            if (m[1] !== undefined) segs.push(m[1]);
            else if (m[2] !== undefined) segs.push(`[${m[2]}]`);
        }
        return segs;
    }

    /** True when jsonPath points at a leaf value in the current payload
     *  (string / number / boolean / null). Containers (object / array) are
     *  considered "non-leaf". */
    private isLeafPath(jsonPath: string): boolean {
        const segs = this.jsonPathToColumnSegments(jsonPath);
        let cur: any = this.parsedJson();
        for (const seg of segs) {
            if (cur === null || cur === undefined) return true;
            if (seg.startsWith('[')) {
                const idx = parseInt(seg.slice(1, -1), 10);
                cur = Array.isArray(cur) ? cur[idx] : undefined;
            } else {
                cur = cur[seg];
            }
        }
        return cur === null || cur === undefined || typeof cur !== 'object';
    }
    /** Live JSON parse error for the textarea — non-null disables the
     *  Save button + the "Done" toggle. */
    readonly jsonError = signal<string | null>(null);
    /** Active tab index (0 = Info, 1 = Payload). Persisted to localStorage
     *  so reopening the page lands on the same tab the operator was on. */
    readonly activeTab = signal<number>(this.readActiveTab());

    constructor() {
        super();
        this.form = this.fb.group({
            type:              [{ value: '', disabled: false },
                                [Validators.required, Validators.maxLength(64)]],
            key:               [{ value: '', disabled: false },
                                [Validators.required, Validators.maxLength(255)]],
            label:             ['',   [Validators.maxLength(1024)]],
            data:              ['{}', []],
            isResource:        [false],
            capacity:          [null,  [Validators.min(0)]],
            reusable:          [null],
            capacityRemaining: [null,  [Validators.min(0)]],
            availability:      [''],
            costPerUse:        [null,  [Validators.min(0)]],
            costCurrency:      ['',    [Validators.maxLength(3)]],
            department:        ['',    [Validators.maxLength(64)]],
            orgUnit:           ['',    [Validators.maxLength(64)]],
            financialUnit:     ['',    [Validators.maxLength(64)]],
        });

        // Bridge `data` control into a signal so parsedJson() recomputes on
        // every schema-form-driven merge. Initial value primes the signal
        // because valueChanges only fires AFTER setValue.
        this.dataText = toSignal(
            this.form.controls['data'].valueChanges,
            { initialValue: this.form.controls['data'].value as string });
        this.parsedJson = computed<unknown>(() => {
            const raw = this.dataText() ?? '';
            if (!raw.trim()) return null;
            try { return JSON.parse(raw); }
            catch { return null; }
        });

        // Surface JSON parse errors live so the Save button can disable
        // when the textarea is mid-edit and not yet valid.
        this.form.controls['data'].valueChanges.subscribe((v: string) => {
            this.jsonError.set(this.validateJson(v));
        });
        this.jsonError.set(this.validateJson(this.form.controls['data'].value));

        // Type-change drives the attribute fetch that powers schema-form.
        this.form.controls['type'].valueChanges.subscribe((v: string) => {
            this.fetchAttributesFor(v);
        });
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            const id = params.get('id');
            if (id) {
                // Editing an existing row needs userdata:update.
                if (!this.permissionChecker.hasAccessToUpdateUserData()) {
                    this.showAccessDeny();
                    this.backToList();
                    return;
                }
                this.mode.set('edit');
                this.loadById(Number(id));
            } else {
                // Creating a new row needs userdata:create.
                if (!this.permissionChecker.hasAccessToCreateUserData()) {
                    this.showAccessDeny();
                    this.backToList();
                    return;
                }
                this.mode.set('create');
                const presetType = this.route.snapshot.queryParamMap.get('type');
                if (presetType) {
                    this.form.controls['type'].setValue(presetType);
                }
            }
        });
    }

    private loadById(id: number): void {
        this.loading.set(true);
        this.userDataService.getById(id, {
            onStart: () => {},
            onSuccess: (row) => {
                this.loaded.set(row);
                this.seedForm(row);
            },
            onError: (err) => this.showError(err, 'Failed to load user data'),
            onComplete: () => this.loading.set(false),
        });
    }

    private seedForm(row: UserData): void {
        const seedAny = row as any;
        this.form.patchValue({
            type:              row.type,
            key:               row.key,
            label:             row.label ?? '',
            data:              this.prettyJsonSeed(row.data) ?? '{}',
            isResource:        seedAny.isResource ?? row.resource ?? false,
            capacity:          seedAny.capacity ?? null,
            reusable:          seedAny.reusable ?? null,
            capacityRemaining: seedAny.capacityRemaining ?? null,
            availability:      seedAny.availability ?? '',
            costPerUse:        seedAny.costPerUse ?? null,
            costCurrency:      seedAny.costCurrency ?? '',
            department:        seedAny.department ?? '',
            orgUnit:           seedAny.orgUnit ?? '',
            financialUnit:     seedAny.financialUnit ?? '',
        });
        this.form.controls['type'].disable();
        this.form.controls['key'].disable();
        this.fetchAttributesFor(row.type);
    }

    onSave(): void {
        if (!this.canSave()) return;
        const needed = this.mode() === 'create'
            ? this.permissionChecker.hasAccessToCreateUserData()
            : this.permissionChecker.hasAccessToUpdateUserData();
        if (!needed) { this.showAccessDeny(); return; }
        const v = this.form.getRawValue();
        const isResource = !!v.isResource;
        const availabilityRaw = (v.availability ?? '').trim();
        const out: UserDataUpsertRequest = {
            type:              v.type.trim(),
            key:               v.key.trim(),
            label:             v.label?.trim() || undefined,
            data:              v.data?.trim() || undefined,
            isResource,
            capacity:          isResource ? this.numOrUndef(v.capacity)           : undefined,
            reusable:          isResource ? (v.reusable === null ? undefined : !!v.reusable) : undefined,
            capacityRemaining: isResource ? this.numOrUndef(v.capacityRemaining)  : undefined,
            availability:      isResource ? (availabilityRaw || undefined)        : undefined,
            costPerUse:        isResource ? this.numOrUndef(v.costPerUse)         : undefined,
            costCurrency:      isResource
                                   ? (v.costCurrency?.trim().toUpperCase() || undefined)
                                   : undefined,
            department:        v.department?.trim() || undefined,
            orgUnit:           v.orgUnit?.trim() || undefined,
            financialUnit:     v.financialUnit?.trim() || undefined,
        };
        this.saving.set(true);
        const handler = {
            onStart: () => {},
            onSuccess: (saved: UserData) => {
                this.showSuccess(`User data ${saved.type}:${saved.key} ${this.mode() === 'create' ? 'created' : 'updated'}`);
                this.backToList();
            },
            onError: (e: string) => this.showError(e),
            onComplete: () => this.saving.set(false),
        };
        if (this.mode() === 'create') {
            this.userDataService.upsert(out, handler);
        } else {
            this.userDataService.put(out.type, out.key, out, handler);
        }
    }

    onReset(): void {
        if (this.mode() === 'edit' && this.loaded()) {
            this.seedForm(this.loaded()!);
            return;
        }
        const presetType = this.route.snapshot.queryParamMap.get('type') ?? '';
        this.form.reset({
            type: presetType,
            key: '',
            label: '',
            data: '{}',
            isResource: false,
            capacity: null,
            reusable: null,
            capacityRemaining: null,
            availability: '',
            costPerUse: null,
            costCurrency: '',
            department: '',
            orgUnit: '',
            financialUnit: '',
        });
        this.form.controls['type'].enable();
        this.form.controls['key'].enable();
    }

    backToList(): void {
        this.router.navigate(['/scheduling/user-data']);
    }

    canSave(): boolean {
        const permitted = this.mode() === 'create'
            ? this.permissionChecker.hasAccessToCreateUserData()
            : this.permissionChecker.hasAccessToUpdateUserData();
        return permitted && this.form.valid && this.jsonError() === null && !this.saving();
    }

    /** Schema-form (focusPath) — store the path; the JSON viewer reads it. */
    onSchemaFieldFocus(path: string): void {
        this.focusedPath.set(path);
    }

    /** Schema-form (pathCleared) — delete the leaf at the emitted JSON
     *  path directly. Bypasses the normal value-flow because patchValue
     *  doesn't unset keys absent from its input, which would leave nested
     *  cleared fields stuck on their stale value. */
    onPathCleared(jsonPath: string): void {
        const segs = this.parseJsonPath(jsonPath);
        if (!segs.length) return;

        const ctl = this.form.controls['data'];
        let current: any = {};
        try {
            current = ctl.value ? JSON.parse(ctl.value) : {};
            if (current === null || typeof current !== 'object' || Array.isArray(current)) {
                current = {};
            }
        } catch { current = {}; }

        this.deleteAtPath(current, segs as any);
        const next = JSON.stringify(current, null, 2);
        if (ctl.value === next) return;
        ctl.setValue(next);
    }

    /** Parse "$.interfaces[0].name" into ["interfaces", 0, "name"]. Mixed
     *  string/number segments — numbers denote array indices. */
    private parseJsonPath(jsonPath: string): (string | number)[] {
        const rest = jsonPath.startsWith('$') ? jsonPath.slice(1) : jsonPath;
        const segs: (string | number)[] = [];
        const regex = /\.([^.\[\]]+)|\[(\d+)\]/g;
        let m: RegExpExecArray | null;
        while ((m = regex.exec(rest)) !== null) {
            if (m[1] !== undefined) segs.push(m[1]);
            else if (m[2] !== undefined) segs.push(Number(m[2]));
        }
        return segs;
    }

    /** Pencil/check toggle on the JSON sub-panel — flips between the
     *  read-only json-path-viewer and the editable textarea. Refuses to
     *  return to viewer when the current JSON doesn't parse. */
    toggleJsonEdit(): void {
        if (this.editingJson()) {
            if (this.jsonError() !== null) return;
            this.editingJson.set(false);
        } else {
            this.editingJson.set(true);
        }
    }

    /** Tab change handler — persists the active tab so the operator
     *  doesn't lose context on reload. */
    onTabChange(index: number): void {
        this.activeTab.set(index);
        try { localStorage.setItem(UserDataEditPageComponent.TAB_KEY, String(index)); }
        catch { /* localStorage unavailable — fine, just in-memory */ }
    }

    private static readonly TAB_KEY = 'ts.user-data-edit.tab';
    private readActiveTab(): number {
        try {
            const v = Number(localStorage.getItem(UserDataEditPageComponent.TAB_KEY));
            return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 0;
        } catch { return 0; }
    }

    // ── Schema synthesis ────────────────────────────────────────────────────

    private fetchAttributesFor(type: string | null | undefined): void {
        const t = (type ?? '').trim();
        if (!t) { this.attributes.set([]); return; }
        this.userDataTypeService.listAttributes({
            onStart: () => {},
            onSuccess: (rows) => {
                // Overlay client-side readOnly state (localStorage) — the
                // backend doesn't yet have a `read_only` column.
                const merged = (rows ?? []).map(r => ({
                    ...r,
                    readOnly: this.readOnlyStore.isReadOnly(t, r.name) || (r as any).readOnly === true,
                }));
                this.attributes.set(merged);
            },
            onError:   () => this.attributes.set([]),
            onComplete: () => {},
        }, t);
    }

    /** Build a JSON-schema fragment from the union of:
     *  1. The type's declared attributes (typed via valueType).
     *  2. Keys observed in the current payload (inferred type from the
     *     runtime value — string / integer / number / boolean / object).
     *  Declared attributes win on type when both sources cover the same
     *  path; payload-only keys default to whatever shape the current value
     *  suggests so the operator can keep editing.
     *
     *  Nested objects in the payload contribute their leaf paths too
     *  (mirrors how declared attributes can have nested payloadPaths).
     *  Arrays of primitives / objects fall back to a string field for v1
     *  — the operator opens the raw-JSON textarea on the left to edit
     *  those. */
    private buildSchema(attrs: UserDataTypeAttribute[], payload: any): any {
        const root: any = { type: 'object', properties: {}, required: [] };

        // 1. Declared attributes
        for (const a of attrs) {
            const raw = (a.payloadPath ?? '').trim();
            if (!raw) continue;
            const path = raw.startsWith('$.') ? raw.slice(2) : raw;
            const parts = path.split('.').filter(p => p.length > 0);
            if (!parts.length) continue;
            let cursor = root.properties;
            for (let i = 0; i < parts.length - 1; i++) {
                const seg = parts[i];
                if (!cursor[seg] || cursor[seg].type !== 'object') {
                    cursor[seg] = { type: 'object', title: seg, properties: {} };
                }
                cursor = cursor[seg].properties;
            }
            const leaf = parts[parts.length - 1];
            cursor[leaf] = {
                type: a.valueType === 'integer' ? 'integer' : 'string',
                title: a.name || leaf,
                description: a.description,
                readOnly: (a as any).readOnly === true,
            };
            if (a.valueType === 'semver') {
                cursor[leaf].pattern = '^[0-9]+(\\.[0-9]+){0,2}([-+].+)?$';
            }
        }

        // 2. Payload-detected keys — only ADD ones the declared set didn't
        //    already cover. Declared rules' typing is authoritative.
        this.mergePayloadKeys(root.properties, payload);

        return root;
    }

    /** Recursively walk a payload object and add / refine { type, title }
     *  entries on `target` for each key. Rules:
     *   • new key  → add with inferred type (object recurses; array gets
     *                a typed buildArraySchema).
     *   • existing scalar + payload object → keep declared scalar (caller
     *     declared a JSON-string field for a complex blob; respect that).
     *   • existing scalar + payload ARRAY → upgrade to an array schema,
     *     using the declared scalar's type as the array's items type. The
     *     payload shape wins on the *container* dimension; the declared
     *     rule still drives item validation. Fixes the v1 bug where
     *     vlanList declared as a string attribute rendered as a plain
     *     text input even though the runtime value is [200, 201, 202].
     *   • existing object + payload object → merge nested keys.
     *   • existing array → leave alone (already correctly typed). */
    private mergePayloadKeys(target: Record<string, any>, payload: any): void {
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;
        for (const key of Object.keys(payload)) {
            const v = payload[key];
            const existing = target[key];
            const inferredType = this.inferType(v);

            if (!existing) {
                if (inferredType === 'object') {
                    target[key] = { type: 'object', title: key, properties: {} };
                    this.mergePayloadKeys(target[key].properties, v);
                } else if (inferredType === 'array') {
                    target[key] = this.buildArraySchema(key, v);
                } else {
                    target[key] = { type: inferredType, title: key };
                }
                continue;
            }

            if (inferredType === 'array' && existing.type !== 'array') {
                // Payload says array, schema says scalar — payload wins on
                // container shape. Promote the declared scalar to the
                // array's items.type so per-item constraints carry through.
                const declaredItemType = (existing.type === 'integer'
                                           || existing.type === 'number'
                                           || existing.type === 'boolean')
                    ? existing.type
                    : 'string';
                const built = this.buildArraySchema(key, v);
                built.items = { ...built.items, type: declaredItemType };
                if (existing.pattern) built.items.pattern = existing.pattern;
                if (existing.title)   built.title = existing.title;
                target[key] = built;
                continue;
            }

            if (existing.type === 'object' && existing.properties
                && inferredType === 'object') {
                this.mergePayloadKeys(existing.properties, v);
            }
            // Otherwise existing scalar wins on type — declared attribute
            // rules are authoritative for the leaves we already know about.
        }
    }

    /** Build a JSON-schema fragment for an array field, choosing the items
     *  type from the array's first non-null element:
     *  ['a','b']            → { type: 'array', items: { type: 'string'  }, x-options.kind: 'primitives' }
     *  [1, 2, 3]            → { type: 'array', items: { type: 'integer' }, x-options.kind: 'primitives' }
     *  [{port:1}, {port:2}] → { type: 'array', items: { type: 'object', properties: {…} },
     *                            x-options.kind: 'objects' }
     *
     *  The custom 'x-options.kind' marker is consumed by the template's
     *  array-of-objects fallback (the schema-form widget renders chip-grid
     *  for primitive arrays; objects fall through to the raw-JSON path).
     *  The properties of the object element are seeded by walking the
     *  first element's keys — subsequent elements with additional keys
     *  trigger a follow-up merge so the schema "grows" as more keys are
     *  observed across the array. */
    private buildArraySchema(key: string, arr: any[]): any {
        if (!arr.length) {
            return { type: 'array', title: key, items: { type: 'string' },
                     'x-options': { kind: 'primitives' } };
        }
        // Find the first non-null element to seed the items shape; ignore
        // mixed primitives + objects (default to string in that case).
        const sample = arr.find(e => e !== null && e !== undefined) ?? '';
        const sampleType = this.inferType(sample);
        if (sampleType === 'object') {
            const itemProps: Record<string, any> = {};
            for (const el of arr) {
                if (el && typeof el === 'object' && !Array.isArray(el)) {
                    this.mergePayloadKeys(itemProps, el);
                }
            }
            return {
                type: 'array', title: key,
                items: { type: 'object', properties: itemProps },
                'x-options': { kind: 'objects' },
            };
        }
        if (sampleType === 'array') {
            // Nested array — too rich for the v1 schema-form widget; fall
            // back to a string-chip so it shows SOMETHING editable.
            return { type: 'array', title: key, items: { type: 'string' },
                     'x-options': { kind: 'primitives' } };
        }
        return {
            type: 'array', title: key,
            items: { type: sampleType },
            'x-options': { kind: 'primitives' },
        };
    }

    private inferType(v: any): 'string' | 'integer' | 'number' | 'boolean' | 'object' | 'array' {
        if (Array.isArray(v)) return 'array';
        if (typeof v === 'boolean') return 'boolean';
        if (typeof v === 'number')  return Number.isInteger(v) ? 'integer' : 'number';
        if (v !== null && typeof v === 'object') return 'object';
        // nulls + everything else → string (operator handles via the raw-
        // JSON textarea on the left sub-panel)
        return 'string';
    }

    /** Schema-form (valueChange) — projects the schema's declared paths
     *  from the form value into the raw `data` JSON. For each declared
     *  path:
     *    • value present + non-empty → write into JSON
     *    • value absent or empty     → delete from JSON
     *  Non-declared keys in the JSON are left untouched, so ad-hoc fields
     *  the type doesn't declare (or that the operator typed directly into
     *  the JSON textarea) survive a schema-form edit.
     *
     *  This is the contract the operator expects: clearing a labelled
     *  input REMOVES the corresponding key from the JSON, instead of
     *  leaving it lingering as an empty string. */
    onSchemaFormChange(value: any): void {
        const schema = this.schemaForType();
        if (!schema || !schema.properties) return;

        const ctl = this.form.controls['data'];
        let current: any = {};
        try {
            current = ctl.value ? JSON.parse(ctl.value) : {};
            if (current === null || typeof current !== 'object' || Array.isArray(current)) {
                current = {};
            }
        } catch { current = {}; }

        const declaredPaths = this.collectLeafPaths(schema.properties, []);
        for (const path of declaredPaths) {
            const nextVal = this.getAtPath(value, path);
            const isEmpty = nextVal === undefined || nextVal === null
                         || (typeof nextVal === 'string' && nextVal === '')
                         || (Array.isArray(nextVal) && nextVal.length === 0);
            if (isEmpty) {
                this.deleteAtPath(current, path);
            } else {
                this.setAtPath(current, path, nextVal);
            }
        }
        const next = JSON.stringify(current, null, 2);
        // No-op guard — skip setValue when the merged JSON matches the
        // current control value. Stops the feedback loop where schema-form
        // re-emits (valueChange) after applyInitialValue, which would
        // otherwise round-trip through data → parsedJson → schemaValue →
        // back into schema-form forever.
        if (ctl.value === next) return;
        ctl.setValue(next);
    }

    /** Walk schema.properties and return every leaf field's path (array of
     *  segments). Object containers contribute their nested leaves rather
     *  than themselves so we never set/delete a whole sub-tree by accident. */
    private collectLeafPaths(properties: Record<string, any>, prefix: string[]): string[][] {
        const out: string[][] = [];
        for (const key of Object.keys(properties)) {
            const field = properties[key];
            const here = [...prefix, key];
            if (field && field.type === 'object' && field.properties) {
                out.push(...this.collectLeafPaths(field.properties, here));
            } else {
                out.push(here);
            }
        }
        return out;
    }

    private getAtPath(obj: any, path: string[]): any {
        let cursor = obj;
        for (const p of path) {
            if (cursor === null || cursor === undefined) return undefined;
            if (typeof cursor !== 'object') return undefined;
            cursor = cursor[p];
        }
        return cursor;
    }

    private setAtPath(obj: any, path: string[], value: any): void {
        if (!path.length) return;
        let cursor = obj;
        for (let i = 0; i < path.length - 1; i++) {
            const seg = path[i];
            if (cursor[seg] === null || cursor[seg] === undefined
                || typeof cursor[seg] !== 'object' || Array.isArray(cursor[seg])) {
                cursor[seg] = {};
            }
            cursor = cursor[seg];
        }
        cursor[path[path.length - 1]] = value;
    }

    /** Delete a leaf at `path`. Also prunes any parent objects that become
     *  empty as a result — declared paths shouldn't leave behind stub
     *  containers like {"metadata": {}} when their only leaf was cleared.
     *  Numeric segments are array indices and use splice (so the array
     *  doesn't end up with a sparse hole). */
    private deleteAtPath(obj: any, path: (string | number)[]): void {
        if (!path.length) return;
        const stack: any[] = [obj];
        let cursor: any = obj;
        for (let i = 0; i < path.length - 1; i++) {
            const seg = path[i];
            if (cursor === null || cursor === undefined || typeof cursor !== 'object') return;
            cursor = cursor[seg as any];
            stack.push(cursor);
        }
        if (cursor === null || cursor === undefined || typeof cursor !== 'object') return;
        const last = path[path.length - 1];
        if (Array.isArray(cursor) && typeof last === 'number') {
            cursor.splice(last, 1);
        } else {
            delete cursor[last as any];
        }
        // Prune empty parents on the way back up — but stop at the root.
        for (let i = stack.length - 1; i > 0; i--) {
            const node = stack[i];
            if (node && typeof node === 'object' && !Array.isArray(node)
                && Object.keys(node).length === 0) {
                const parent = stack[i - 1];
                const seg = path[i - 1];
                if (parent && typeof parent === 'object') {
                    if (Array.isArray(parent) && typeof seg === 'number') {
                        parent.splice(seg, 1);
                    } else {
                        delete parent[seg as any];
                    }
                }
            } else {
                break;
            }
        }
    }

    private prettyJsonSeed(raw: string | undefined | null): string | undefined {
        if (!raw || !raw.trim()) return raw ?? undefined;
        try { return JSON.stringify(JSON.parse(raw), null, 2); }
        catch { return raw; }
    }

    private validateJson(value: string): string | null {
        if (!value || !value.trim()) return null;
        try { JSON.parse(value); return null; }
        catch (e: any) { return e?.message ?? 'Invalid JSON'; }
    }

    private numOrUndef(value: unknown): number | undefined {
        if (value === null || value === undefined || value === '') return undefined;
        const n = Number(value);
        return Number.isFinite(n) ? n : undefined;
    }
}
